import { observer } from 'mobx-react-lite'
import { Fragment } from 'react'
import { Item } from '@react-stately/collections'
import Styles from './ContextMenus.module.scss'
import { Menu } from 'components/Menu/Menu'
import { ActionKey, BasePopoverMenuProps, isActionKey } from './Types'
import { NavItem } from 'components/NavItem'
import { MoreThick } from 'almanac-ui/icons'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { VisualSwitch } from 'components/VisualSwitch/VisualSwitch'
import { LocationPicker, LocationPickerFolder, SelectedFolder } from 'components/LocationPicker/LocationPicker'
import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { getAllDocsPath, getFolderPath } from 'utils/urlUtils'
import { showSuccessfulMoveToast } from 'common/ToastMessage/showMoveToast'
import { moveError } from 'components/NavRightClickMenu/components/MoveError/MoveError'
import { useRootStore } from 'stores/RootStore'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { HandbookTreeItem } from '../Stores/HandbookTreeItem'
import { useDispatch } from 'react-redux'
import { setMoveModalVisible } from 'redux/actions'
import { useSelector } from 'react-redux'
import { RootState } from 'types'
import { isEqual } from 'lodash'
import { useQueryClient } from 'react-query'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useKey } from 'react-use'
import { useEvaluateMove } from 'components/LocationPicker/hooks/useEvaluateMove'

export const BasePopoverMenu = observer(({ menus, handleAction, disabledKeys, item }: BasePopoverMenuProps) => {
  const workspaceId = useCurrentWorkspaceId()
  const rootStore = useRootStore()
  const dispatch = useDispatch()
  const moveModalVisible = useSelector(({ moveModalVisible }: RootState) => moveModalVisible, isEqual)
  const queryClient = useQueryClient()
  const { evaluateMove } = useEvaluateMove()

  let selectedFiles = []
  let treeItem: HandbookTreeItem | undefined
  let currentFolder: LocationPickerFolder | null = null

  useKey('Escape', (e) => {
    dispatch(setMoveModalVisible(false))
  })

  if (item) {
    treeItem = rootStore.handbooks.items.get(item.id.toString())

    if (treeItem) {
      selectedFiles = treeItem.data.document ? [treeItem.data.document] : [treeItem.data.folder]
      const folder = treeItem.parentHandbook?.handbook.folder

      if (folder) {
        currentFolder = {
          id: folder.key,
          title: folder.title,
          ancestor_keys: folder.almanac_file.ancestors.map((a) => a.id),
        }
      }
    }
  }

  const moveFiles = async ({
    selectedFolder,
    newWorkspaceId,
  }: {
    selectedFolder: SelectedFolder | null
    newWorkspaceId: number
  }) => {
    if (!item || !treeItem) return

    const numFiles = selectedFiles.length
    const selectedFolderId = !!selectedFolder ? selectedFolder.id : null
    const folderKeys: string[] = item.folder ? [item.folder.key] : []
    const articleKeys: string[] = item.document ? [item.document.key] : []

    const dismissLoadingToast = showToastMessage('Moving...')
    const response = await FolderClient.bulkMoveFiles({
      folderIds: folderKeys,
      articleIds: articleKeys,
      fileIds: [],
      destinationFolderId: selectedFolderId,
      destinationWorkspaceId: newWorkspaceId,
      originalWorkspaceId: workspaceId,
    })
    dismissLoadingToast()

    if (response.type === SUCCESS_RESPONSE_TYPE) {
      const url = selectedFolder?.seo_string
        ? getFolderPath({ folderSlug: selectedFolder.seo_string })
        : getAllDocsPath(newWorkspaceId)

      dispatch(setMoveModalVisible(false))
      showSuccessfulMoveToast(numFiles, url, selectedFolder?.title)

      if (item.folder) rootStore.files.fetch(item.folder.key, true)

      if (treeItem.parentHandbook?.handbook?.key) {
        rootStore.handbooks.refresh(treeItem.parentHandbook?.handbook?.key)
      } else {
        // Coming from root so we need to remove from the top-level
        queryClient.invalidateQueries(['workspace', workspaceId, 'files', 'personal'])
      }

      if (selectedFolder) {
        rootStore.handbooks.refresh(selectedFolder.handbook_key)
      } else {
        // Remove because it's going to the top-level and will no longer be a handbook item
        rootStore.handbooks.removeItem(treeItem.id)
      }
      return
    }
    if (response.type === 'error') {
      moveError({ status: response.status, data: response.data })
    }
    dispatch(setMoveModalVisible(false))
  }

  return (
    <DropdownTrigger
      placement="bottom"
      onClose={() => {
        dispatch(setMoveModalVisible(false))
      }}
    >
      <NavItem.Control aria-label="Options" icon={MoreThick} />
      {(close) => {
        if (moveModalVisible && item) {
          return (
            <LocationPicker
              fileTitle={item.title}
              actionType="move"
              initialFolder={currentFolder}
              onSelectFolder={async (payload) => {
                const folderIds: string[] = item.folder ? [item.folder.key] : []
                const articleIds: string[] = item.document ? [item.document.key] : []

                await evaluateMove({
                  onSelectFolderPayload: payload,
                  fileMoveAttemptPayload: {
                    folderIds,
                    articleIds,
                    originalWorkspaceId: workspaceId,
                  },
                  handleMove: moveFiles,
                })

                close()
              }}
              initialWorkspaceId={workspaceId}
              isRootDisabled={!!item.document || !currentFolder}
              disabledFolderIds={item.folder ? [item.folder.key] : []}
            />
          )
        }

        return (
          <>
            {menus.map((menuItems, idx) => (
              <Fragment key={`menu-${idx}`}>
                {idx > 0 && <hr className={Styles.divider} />}
                <Menu
                  aria-label="Menu"
                  selectionMode="single"
                  onAction={(key) => {
                    if (isActionKey(key)) {
                      handleAction(key)
                      if (key !== ActionKey.move) close()
                    }
                  }}
                  disabledKeys={disabledKeys}
                  showCheckmark={false}
                  itemClassName={Styles.menuItem}
                >
                  {menuItems.map(({ Icon, key, ...itemProps }) => (
                    <Item {...itemProps} key={key}>
                      <Icon aria-hidden="true" />
                      {itemProps.textValue}
                      {itemProps.canToggle && (
                        <div className={Styles.toggle}>
                          <VisualSwitch active={itemProps.isToggled || false} variant="sm" />
                        </div>
                      )}
                    </Item>
                  ))}
                </Menu>
              </Fragment>
            ))}
          </>
        )
      }}
    </DropdownTrigger>
  )
})
